import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "../../components/Card";
import Modal from "../../components/Modal";
import Avatar from "../../components/Dashboard/Avatar";
import Chip from "../../components/Dashboard/Chip";
import Timeline from "../../components/Dashboard/Timeline";
import useModalStack from "../../components/Dashboard/useModalStack";
import {
  getAttribution,
  listAttributions,
  reassignAttribution,
  reviewAttribution,
} from "../../api/referrals.admin.service";
import {
  askReason,
  CursorPager,
  errorCodeOf,
  isOk,
  JsonBlock,
  PLATFORM_LABEL,
  StatusBadge,
  useCursorList,
  useErrorText,
  useFormat,
} from "./shared";
import styles from "./Referrals.module.sass";

const PROGRAMS = ["FRIEND", "PARTNER", "PROMO"];
const STATUSES = ["ATTRIBUTED", "REVIEW", "CONVERTED", "REJECTED"];
const SOURCES = ["DEEP_LINK", "INSTALL_REFERRER", "MANUAL", "CLIPBOARD"];
const TL_COLORS = { claim: "#2A85FF", status: "#FFB020", reward: "#83BF6E", audit: "#8E59FF", charge: "#2ED0A0", refund: "#FF6A55" };

const personLabel = (p, t) => (p?.anonymized ? t("referrals.attributions.anonymized", { alias: p.alias }) : p?.email || p?.name || p?.id || "—");

/**
 * Atribuciones (06 §2.2): tabla filtrable con cursor y ficha con el timeline
 * completo, los términos congelados, las señales de fraude y las acciones
 * (aprobar o rechazar la revisión, corregir la atribución).
 */
const AttributionsTab = ({ initialStatus }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const [filters, setFilters] = useState({ status: initialStatus || "" });
  const list = useCursorList(listAttributions, { ...filters, limit: 30 });
  const modal = useModalStack();

  useEffect(() => {
    if (initialStatus) setFilters((f) => ({ ...f, status: initialStatus }));
  }, [initialStatus]);

  const set = (key, value) => setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));

  return (
    <>
      <Card className={styles.card}>
        <div className={styles.filters}>
          {PROGRAMS.map((p) => (
            <Chip key={p} on={filters.program === p} onClick={() => set("program", p)}>
              {t(`referrals.program.${p}`)}
            </Chip>
          ))}
          <span className={styles.sep} />
          {STATUSES.map((s) => (
            <Chip key={s} on={filters.status === s} onClick={() => set("status", s)}>
              {t(`referrals.status.${s}`)}
            </Chip>
          ))}
          <span className={styles.sep} />
          {SOURCES.map((s) => (
            <Chip key={s} on={filters.source === s} onClick={() => set("source", s)}>
              {t(`referrals.source.${s}`)}
            </Chip>
          ))}
          <Chip on={filters.flagged === "true"} onClick={() => set("flagged", "true")}>
            ⚑ {t("referrals.attributions.flagged")}
          </Chip>
        </div>

        {list.error && <div className={styles.empty}>{errorText(list.error)}</div>}
        {!list.error && list.items.length === 0 && (
          <div className={styles.empty}>{list.loading ? t("referrals.common.loading") : t("referrals.common.noData")}</div>
        )}
        <div className={styles.table}>
          {list.items.map((a) => (
            <button key={a.id} type="button" className={styles.row} onClick={() => modal.open({ kind: "attribution", id: a.id })}>
              <Avatar user={a.referee?.anonymized ? null : a.referee} label={a.referee?.alias} />
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>{personLabel(a.referee, t)}</div>
                <div className={styles.muted}>
                  <code>{a.code}</code> · {t(`referrals.program.${a.program}`)} · {t(`referrals.source.${a.source}`)}
                  {a.platform ? ` · ${PLATFORM_LABEL[a.platform] ?? a.platform}` : ""}
                  {a.flags?.length ? ` · ⚑ ${a.flags.join(", ")}` : ""}
                </div>
              </div>
              <div className={styles.rowSide}>
                <StatusBadge status={a.status} />
                <div className={styles.muted}>{fmt.dateTime(a.createdAt)}</div>
              </div>
            </button>
          ))}
        </div>
        <CursorPager list={list} />
      </Card>

      <Modal visible={!!modal.current} onClose={modal.close} outerClassName={styles.modalOuter}>
        {modal.current?.kind === "attribution" && (
          <AttributionDetail
            id={modal.current.id}
            canGoBack={modal.canGoBack}
            onBack={modal.back}
            onOpen={(id) => modal.push({ kind: "attribution", id })}
            onChanged={list.reload}
          />
        )}
      </Modal>
    </>
  );
};

const AttributionDetail = ({ id, canGoBack, onBack, onOpen, onChanged }) => {
  const { t } = useTranslation();
  const fmt = useFormat();
  const errorText = useErrorText();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const load = async () => {
    const resp = await getAttribution(id);
    if (isOk(resp)) {
      setData(resp.data);
      setError(null);
    } else setError(errorCodeOf(resp));
  };

  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (fn) => {
    setBusy(true);
    const resp = await fn();
    setBusy(false);
    if (!isOk(resp)) {
      setError(errorCodeOf(resp));
      return null;
    }
    await load();
    onChanged?.();
    return resp.data;
  };

  const review = async (decision) => {
    const note = askReason(t, "referrals.attributions.notePrompt");
    if (decision === "REJECTED" && !note) return;
    await act(() => reviewAttribution(id, decision, note || undefined));
  };

  const reassign = async () => {
    const codeId = window.prompt(t("referrals.attributions.reassignCodePrompt"));
    if (!codeId) return;
    const reason = askReason(t);
    if (!reason) return;
    const created = await act(() => reassignAttribution(id, codeId.trim(), reason));
    if (created?.id) onOpen(created.id);
  };

  if (error && !data) return <div className={styles.empty}>{errorText(error)}</div>;
  if (!data) return <div className={styles.empty}>{t("referrals.common.loading")}</div>;

  const a = data.attribution;
  return (
    <div className={styles.detail}>
      {canGoBack && (
        <button type="button" className={styles.linkBtn} onClick={onBack}>
          ‹ {t("referrals.common.back")}
        </button>
      )}
      <div className={styles.detailHead}>
        <Avatar user={data.referee?.anonymized ? null : data.referee} label={data.referee?.alias} size="lg" />
        <div className={styles.rowMain}>
          <div className={styles.detailTitle}>{personLabel(data.referee, t)}</div>
          <div className={styles.muted}>
            {t("referrals.attributions.byCode", { code: a.code })} · {t(`referrals.program.${a.programKey}`)} v{a.programVersion}
            {data.owner ? ` · ${t("referrals.attributions.owner")}: ${data.owner.email || data.owner.name || data.owner.id}` : ""}
            {data.partner ? ` · ${t("referrals.attributions.partner")}: ${data.partner.displayName}` : ""}
          </div>
          <div className={styles.badges}>
            <StatusBadge status={a.status} />
            {a.fraud?.flags?.map((f) => (
              <span key={f} className={styles.flag}>
                ⚑ {t(`referrals.flags.${f}`, { defaultValue: f })}
              </span>
            ))}
          </div>
        </div>
        {data.referee?.email && (
          <Link className={styles.linkBtn} to={`/users?search=${encodeURIComponent(data.referee.email)}`}>
            {t("referrals.attributions.userLink")} ›
          </Link>
        )}
      </div>

      {error && <div className={styles.errorBox}>{errorText(error)}</div>}

      <div className={styles.actions}>
        {a.status === "REVIEW" && (
          <button type="button" className="button button-small" disabled={busy} onClick={() => review("APPROVED")}>
            {t("referrals.attributions.approve")}
          </button>
        )}
        {(a.status === "REVIEW" || a.status === "ATTRIBUTED") && (
          <button type="button" className="button-stroke button-small" disabled={busy} onClick={() => review("REJECTED")}>
            {t("referrals.attributions.reject")}
          </button>
        )}
        {(a.status === "REVIEW" || a.status === "ATTRIBUTED") && (
          <button type="button" className="button-stroke button-small" disabled={busy} onClick={reassign}>
            {t("referrals.attributions.reassign")}
          </button>
        )}
        {a.reassignedFromId && (
          <button type="button" className={styles.linkBtn} onClick={() => onOpen(a.reassignedFromId)}>
            {t("referrals.attributions.reassignedFrom")} ›
          </button>
        )}
      </div>

      <div className={styles.facts}>
        <div>
          <span className={styles.muted}>{t("referrals.attributions.claimedAt")}</span>
          <strong>{fmt.dateTime(a.createdAt)}</strong>
        </div>
        <div>
          <span className={styles.muted}>{t("referrals.attributions.source")}</span>
          <strong>{t(`referrals.source.${a.source}`)}</strong>
        </div>
        <div>
          <span className={styles.muted}>{t("referrals.attributions.platform")}</span>
          <strong>{[PLATFORM_LABEL[a.platform] ?? a.platform, a.appVersion, a.country].filter(Boolean).join(" · ") || "—"}</strong>
        </div>
        <div>
          <span className={styles.muted}>{t("referrals.attributions.friendPriceUntil")}</span>
          <strong>{a.discount?.eligibleUntil ? fmt.date(a.discount.eligibleUntil) : "—"}</strong>
        </div>
      </div>

      <h4 className={styles.sectionTitle}>{t("referrals.attributions.timeline")}</h4>
      <Timeline
        items={data.timeline.map((e, i) => ({
          key: i,
          color: TL_COLORS[e.type],
          date: fmt.dateTime(e.at),
          sub: e.data?.reason || undefined,
          what: (
            <>
              {t(`referrals.timeline.${e.label.split(":")[0].replace(".", "_")}`, { defaultValue: e.label })}
              <span className={styles.muted}> {e.label.includes(":") ? e.label.slice(e.label.indexOf(":") + 1) : ""}</span>
              {e.data?.days ? <span className={styles.muted}> · {e.data.days} d</span> : null}
              {e.data?.appliedUntil ? <span className={styles.muted}> → {fmt.date(e.data.appliedUntil)}</span> : null}
              {e.type === "charge" ? (
                <span className={styles.muted}>
                  {" "}
                  · {e.data.productId} · {e.data.gross}
                  {e.data.netEurCents != null ? ` · ${t("referrals.timeline.net", { value: fmt.eur(e.data.netEurCents) })}` : ""}
                  {e.data.friendOffer ? ` · ${t("referrals.timeline.friendOffer")}` : ""}
                  {e.data.test ? " · sandbox" : ""}
                </span>
              ) : null}
            </>
          ),
        }))}
      />

      <h4 className={styles.sectionTitle}>
        <button type="button" className={styles.linkBtn} onClick={() => setShowTerms(!showTerms)}>
          {showTerms ? "▾" : "▸"} {t("referrals.attributions.terms")}
        </button>
      </h4>
      {showTerms && <JsonBlock value={a.terms} />}
    </div>
  );
};

export default AttributionsTab;
